import React, { useState, useEffect, useRef } from 'react';
import { X, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../common/Button';
import { useDatabase } from '../../db/DatabaseProvider';
import { useAuthStore } from '../../stores';
import { useToast } from '../common/ToastProvider';
import { overlayStack } from '../../lib/overlayStack';
import { now } from '../../lib/uuid';

export interface VoidTransactionDrawerProps {
  transactionId: string;
  onClose: () => void;
  onVoided?: () => void;
}

export function VoidTransactionDrawer({
  transactionId,
  onClose,
  onVoided,
}: VoidTransactionDrawerProps) {
  const db = useDatabase();
  const toast = useToast();
  const { user, profile } = useAuthStore();

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [consumedSessionsCount, setConsumedSessionsCount] = useState(0);
  const [acknowledgedConsumption, setAcknowledgedConsumption] = useState(false);

  // Check if this transaction has packages with already consumed sessions
  useEffect(() => {
    if (!db || !transactionId) return;

    let isMounted = true;
    db.customer_packages
      .find({ selector: { transaction_id: transactionId } })
      .exec()
      .then((cpkgs) => {
        if (!isMounted) return;
        let totalUsed = 0;
        for (const cpkg of cpkgs) {
          const fromDetails = (cpkg.details || []).reduce((acc: number, d: any) => acc + (d.used_sessions || 0), 0);
          const fromAttentions = (cpkg.attentions || []).length;
          totalUsed += Math.max(fromDetails, fromAttentions);
        }
        setConsumedSessionsCount(totalUsed);
      })
      .catch((err) => {
        console.error('[VoidTransactionDrawer] Error checking customer packages:', err);
      });

    return () => {
      isMounted = false;
    };
  }, [db, transactionId]);

  // Register with overlayStack for ESC key cascade and body scroll lock
  const overlayIdRef = useRef<string | null>(null);
  useEffect(() => {
    overlayIdRef.current = overlayStack.push(onClose);
    return () => {
      if (overlayIdRef.current) {
        overlayStack.remove(overlayIdRef.current);
        overlayIdRef.current = null;
      }
    };
  }, [onClose]);

  const handleConfirmVoid = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setErrorMessage('Debe especificar un motivo obligatorio para la anulación.');
      return;
    }

    if (consumedSessionsCount > 0 && !acknowledgedConsumption) {
      setErrorMessage('Debe confirmar que autoriza la anulación a pesar de las sesiones consumidas.');
      return;
    }

    if (!db) {
      setErrorMessage('La base de datos no está disponible.');
      return;
    }

    if (!transactionId) {
      setErrorMessage('Identificador de transacción no válido.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const timestamp = now();
      const auditUser = user?.email || profile?.name || 'operador-desconocido';

      // 1. Fetch transaction doc from RxDB
      const tx = await db.transactions.findOne(transactionId).exec();
      if (!tx) {
        toast.error('No se encontró el document de la transacción a anular.');
        onClose();
        return;
      }

      if (tx.status === 'voided') {
        toast.warning('Esta transacción ya se encuentra anulada.');
        onVoided?.();
        onClose();
        return;
      }

      // 2. Update transaction doc: status, void_reason, voided_by_user_id, updated_at
      await tx.patch({
        status: 'voided',
        void_reason: trimmedReason,
        voided_by_user_id: auditUser,
        updated_at: timestamp,
        last_modified_by: auditUser,
        
      });

      // 3. Revert client balance (debt reversion + credit balance refund)
      if (tx.customer_id && db.customers) {
        const customerDoc = await db.customers.findOne(tx.customer_id).exec();
        if (customerDoc) {
          const currentDebt = customerDoc.debt_balance || 0;
          const currentCredit = customerDoc.credit_balance || 0;

          const pendingDivisa = tx.pending_balance || 0;
          const newDebt = (tx.payment_status === 'pending' || pendingDivisa > 0)
            ? Math.max(0, currentDebt - pendingDivisa)
            : currentDebt;

          // Refund any credit-balance used in tx.payments
          const creditRefund = (tx.payments || [])
            .filter((p: any) => p.payment_method_id === 'credit-balance')
            .reduce((acc: number, p: any) => acc + (p.base_equivalent ?? p.received_amount ?? 0), 0);

          const newCredit = currentCredit + creditRefund;

          if (newDebt !== currentDebt || newCredit !== currentCredit) {
            await customerDoc.patch({
              debt_balance: newDebt,
              credit_balance: newCredit,
              updated_at: timestamp,
              last_modified_by: auditUser,
              
            });
          }
        }
      }

      // 4. Mark any customer_packages created by this transaction as status: 'anulado'
      if (db.customer_packages) {
        const cpkgs = await db.customer_packages.find({
          selector: { transaction_id: transactionId }
        }).exec();

        for (const cpkg of cpkgs) {
          await cpkg.patch({
            status: 'cancelled',
            updated_at: timestamp,
            last_modified_by: auditUser,
          });
        }
      }

      // 5. Displays success toast and calls onVoided() and onClose()
      toast.success('La venta ha sido anulada con éxito y los saldos han sido revertidos.', 'Venta Anulada');
      onVoided?.();
      onClose();
    } catch (err: any) {
      console.error('[VoidTransactionDrawer] Error anular venta:', err);
      const msg = err?.message || 'Error al procesar la anulación de la venta.';
      setErrorMessage(msg);
      toast.error(msg, 'Fallo al Anular');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="anular-venta-title"
    >
      <div className="bg-surface border border-border shadow-2xl rounded-3xl w-full max-w-md overflow-hidden flex flex-col transform transition-transform duration-200 animate-scale-in">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/60 bg-surface-secondary/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-500 border border-rose-500/20">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div>
              <h3 id="anular-venta-title" className="text-base font-black text-text-primary leading-tight">
                Anular Venta
              </h3>
              <p className="text-xs text-text-secondary mt-0.5">
                ID: {transactionId.substring(0, 12)}...
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl p-2 text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary transition-colors cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
            aria-label="Cerrar modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Body */}
        <form onSubmit={handleConfirmVoid} className="p-6 space-y-5 flex-1 overflow-y-auto custom-scrollbar">
          
          {/* Warning Banner */}
          <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4 flex items-start gap-3 text-rose-700 dark:text-rose-400 text-xs font-semibold">
            <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-extrabold block">Atención: Acción Irreversible</span>
              <p className="text-rose-600 dark:text-rose-300 leading-relaxed">
                Al anular esta venta se revertirá la deuda acumulada del cliente (si era venta a crédito) y se inactivarán los paquetes adquiridos en esta transacción.
              </p>
            </div>
          </div>

          {/* Consumed Sessions Alert */}
          {consumedSessionsCount > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 space-y-2.5 text-xs text-amber-700 dark:text-amber-300 font-semibold animate-fade-in shadow-xs">
              <div className="flex items-center gap-2 font-black text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>Paquete con {consumedSessionsCount} sesión(es) ya consumida(s)</span>
              </div>
              <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">
                Esta venta acreditó un paquete del cual el cliente ya utilizó <strong>{consumedSessionsCount} sesión(es)</strong>. Si anulas la venta, el paquete quedará cancelado pero las atenciones ya realizadas se conservarán en el historial médico.
              </p>
              <label className="flex items-start gap-2.5 pt-1.5 text-[11px] font-bold text-text-primary cursor-pointer select-none border-t border-amber-500/20">
                <input
                  type="checkbox"
                  checked={acknowledgedConsumption}
                  onChange={(e) => setAcknowledgedConsumption(e.target.checked)}
                  className="mt-0.5 rounded text-amber-600 focus:ring-amber-500 border-border cursor-pointer"
                />
                <span>Comprendo el impacto y autorizo anular esta venta con sesiones ya consumidas.</span>
              </label>
            </div>
          )}

          {/* Motivo Input */}
          <div className="space-y-2">
            <label className="block text-xs font-extrabold uppercase tracking-wider text-text-primary">
              Motivo de Anulación <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errorMessage) setErrorMessage(null);
              }}
              placeholder="Indique la razón de la anulación (ej. Error en método de pago, solicitud de cliente, duplicado...)"
              disabled={isSubmitting}
              className="w-full rounded-2xl border border-border bg-surface-secondary/50 p-3.5 text-xs text-text-primary placeholder:text-text-tertiary focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none transition-all shadow-xs custom-scrollbar resize-none"
              autoFocus
            />
            {errorMessage && (
              <p className="text-xs font-bold text-rose-500 animate-fade-in">{errorMessage}</p>
            )}
          </div>

          {/* User Audit Note */}
          <div className="text-[11px] text-text-tertiary bg-surface-secondary/40 border border-border/50 rounded-xl p-3 flex justify-between items-center font-medium">
            <span>Usuario auditor:</span>
            <span className="font-bold text-text-secondary">{user?.email || profile?.name || 'Operador'}</span>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              size="md"
              disabled={isSubmitting || !reason.trim() || (consumedSessionsCount > 0 && !acknowledgedConsumption)}
              className="bg-rose-600 hover:bg-rose-700 text-white font-extrabold border-none shadow-md shadow-rose-600/20 cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Anulando...
                </span>
              ) : (
                'Confirmar Anulación'
              )}
            </Button>
          </div>
        </form>

      </div>
    </div>
  );
}
